import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Popup as AntdMobilePopup } from "antd-mobile";
import { Space } from "antd-mobile";
import { Button } from "../Base/Button";
import { Tabs, Tab } from "../Base/Tabs";
import { Form, FormItem } from "../Base/Form";
import { Checkbox } from "../Base/Checkbox";
import { AntdLink } from "../Base/AntdTypography";
import { Toast } from "../Base/Toast";
import Turnstile from "../Turnstile";
import { triggerAgreementConfirmation } from "../AgreementConfirmation";
import { ProtocolDialog } from "../ProtocolDialog";
import { EnhancedInput } from "../Base/Input/EnhancedInput";
import { ImageCode } from "../Base/ImageCode";
import CountryCodeSelect from "../Base/CountrySelect/CountryCodeSelect";
import { useUserAuth, useUserStore } from "../../modules";
import { useStartupDataFlattened } from "../../modules/startup";
import {
  LoginRequest,
  RegisterRequest,
} from "../../modules/user/types/auth.types";
import { useCountdown } from "../../hooks/useUtils";
import { sendSmsCode } from "../../api/sms";
import { getAuthConfig } from "../../api/auth";
import { useAuthConfigStore } from "../../stores";
import utils from "../../utils/utils";
import { wsUtils } from "../../utils/wsUtils";
import {
  isWebPushSupported,
  subscribeHasLogin,
  getPushSubscriptionStatus,
} from "../../utils/webPush";
import {
  processPhone,
  getPhonePattern,
  validatePhone,
} from "../../utils/phoneValidation";
import { useT } from "../../i18n/hooks";
import {
  useLoginRegisterConfig,
  useResetPasswordIpInCooldown,
  useResetPasswordEmailIpInCooldown,
  useOnlineServiceSupportUrl,
} from "../../modules/homeDisplay";
import { useHomeDisplayStore } from "../../modules/homeDisplay/stores";
import { ResetPasswordDialog } from "../../components/ResetPasswordDialog";
import { ResetPasswordByEmailDialog } from "../../components/ResetPasswordByEmailDialog";
import { PasswordRecoveryDialog } from "../../components/PasswordRecoveryDialog";
import { TooManyPasswordAttemptsDialog } from "../../components/TooManyPasswordAttemptsDialog";
import trackingService from "@/services/trackingService";
import { PhoneIcon, UserIcon } from "../../components/Icon/IconSvgMap";
import { AdUtils } from "../../utils";
import { useNavigation } from "../../utils/navigation";
import {
  LOGIN_REGISTER_POPUP_EVENT,
  CLOSE_LOGIN_REGISTER_POPUP_EVENT,
} from "./triggerLoginRegisterPopup";
import type {
  LoginRegisterPopupProps,
  LoginRegisterFormValues,
  LoginRegisterTab,
  LoginRegisterMode,
  OpenLoginRegisterPopupOptions,
} from "./LoginRegisterPopup.types";
import { FooterCooperationPlatformsTopRow } from "../Footer/FooterCooperationPlatforms";
import "../Footer/FooterCooperationPlatforms.scss";
import "./LoginRegisterPopup.scss";

export const LoginRegisterPopup: React.FC<LoginRegisterPopupProps> = ({
  className = "",
}) => {
  const t = useT();
  const navigate = useNavigation();
  const { login, loginLoading, register, registerLoading } = useUserAuth();
  const { enableWebPush } = useStartupDataFlattened();
  const isAuthenticated = useUserStore(state => state.isAuthenticated());

  const loginRegisterConfig = useLoginRegisterConfig();
  const resetPasswordIpInCooldown = useResetPasswordIpInCooldown();
  const resetPasswordEmailIpInCooldown = useResetPasswordEmailIpInCooldown();
  const onlineServiceSupportUrl = useOnlineServiceSupportUrl();

  const [visible, setVisible] = useState(false);
  const [fromPath, setFromPath] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [mode, setMode] = useState<LoginRegisterMode>("login");
  const [loginMethod, setLoginMethod] = useState<LoginRegisterTab>("password");
  const [showPassword, setShowPassword] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, boolean>>({});
  const [wafToken, setWafToken] = useState<string>("");
  const [imageCode, setImageCode] = useState<string>("");
  const [selectedCountryId, setSelectedCountryId] = useState<string>(
    utils.getObject("countryId")
  );
  const [selectedPhoneCode, setSelectedPhoneCode] = useState<string>("");
  const [protocolDialogVisible, setProtocolDialogVisible] = useState(false);
  const [currentProtocolPath, setCurrentProtocolPath] = useState("");
  const [resetPasswordVisible, setResetPasswordVisible] = useState(false);
  const [inviteCode, setInviteCode] = useState<string>("");
  const [resetPasswordByEmailVisible, setResetPasswordByEmailVisible] =
    useState(false);
  const [passwordRecoveryVisible, setPasswordRecoveryVisible] = useState(false);
  const [tooManyAttemptsVisible, setTooManyAttemptsVisible] = useState(false);

  const { timeLeft, isActive, start, reset } = useCountdown(60);
  const { enableWafCode, wafSiteKey, formKey, disablePhone, setAuthConfig } =
    useAuthConfigStore();

  const enableLoginMobileVerifyCode =
    loginRegisterConfig?.enableLoginMobileVerifyCode ?? false;

  const enableLoginImageCode =
    loginRegisterConfig?.enableLoginImageCode ?? false;

  const enableRegisterImageCode =
    loginRegisterConfig?.enableRegisterImageCode ?? false;

  const enableProtocolText =
    loginRegisterConfig?.enableDefaultCheckProtocol ?? false;

  const defaultProtocolText =
    loginRegisterConfig?.defaultProtocolText ??
    "I confirm that I am 21 years old and I have read the [Terms of use](/protocol/terms) and [Privacy policy](/protocol/privacy)";

  const parseMarkdownLinks = (text: string): string => {
    return text.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="javascript:void(0)" data-protocol-path="$2">$1</a>'
    );
  };

  const protocolHtml = parseMarkdownLinks(defaultProtocolText);

  const handleProtocolLinkClick = (e: React.MouseEvent<HTMLSpanElement>) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "A" && target.hasAttribute("data-protocol-path")) {
      e.preventDefault();
      const path = target.getAttribute("data-protocol-path");
      if (path) {
        setCurrentProtocolPath(path);
        setProtocolDialogVisible(true);
      }
    }
  };

  const enableCodeLogin = useMemo(() => {
    if (enableLoginMobileVerifyCode) return true;
    if (disablePhone) return false;
    return false;
  }, [enableLoginMobileVerifyCode, disablePhone]);

  useEffect(() => {
    const loadAuthConfig = async () => {
      try {
        const response = await getAuthConfig();
        if (response) {
          setAuthConfig({
            enableWafCode: response.enableWafCode || false,
            wafSiteKey: response.wafSiteKey || "",
            formKey: response.formKey || "",
            disablePhone: response.disablePhone || false,
          });
        }
      } catch (error) {
        console.error("Failed to load auth config:", error);
      }
    };
    if (visible) {
      loadAuthConfig();
    }
  }, [visible, setAuthConfig]);

  useEffect(() => {
    const countryId = utils.getObject("countryId");
    const country = window._config?.countryList?.find(c => c.id === countryId);
    if (country && !selectedPhoneCode) {
      setSelectedPhoneCode(country.phoneCode);
      setSelectedCountryId(country.id);
    }
  }, [selectedPhoneCode]);

  const phonePattern = useMemo(
    () => getPhonePattern(selectedCountryId),
    [selectedCountryId]
  );

  useEffect(() => {
    if (visible) {
      const searchParams = new URLSearchParams(location.search);
      const pParam = searchParams.get("p") || utils.getObject("invitedCode");
      if (pParam) {
        setInviteCode(pParam);
      }
    }
  }, [visible]);

  // Listen for open/close events
  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenLoginRegisterPopupOptions>)
        .detail;
      const newMode = detail?.defaultMode ?? "login";
      setMode(newMode);
      if (newMode === "login") {
        setLoginMethod(detail?.defaultTab ?? "password");
      }
      if (detail?.from) {
        setFromPath(detail.from);
      }
      setVisible(true);
    };

    const handleClose = () => {
      setVisible(false);
    };

    window.addEventListener(LOGIN_REGISTER_POPUP_EVENT, handleOpen);
    window.addEventListener(CLOSE_LOGIN_REGISTER_POPUP_EVENT, handleClose);
    return () => {
      window.removeEventListener(LOGIN_REGISTER_POPUP_EVENT, handleOpen);
      window.removeEventListener(CLOSE_LOGIN_REGISTER_POPUP_EVENT, handleClose);
    };
  }, []);

  // Auto-close when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && visible) {
      setVisible(false);
    }
  }, [isAuthenticated, visible]);

  const handleFieldsChange = useCallback(() => {
    setTimeout(() => {
      const values = form.getFieldsValue();
      const errors = form.getFieldsError();
      const hasErrors = errors.some((field: any) => field.errors.length > 0);
      let hasEmptyFields = false;
      if (mode === "register") {
        hasEmptyFields = !values.mobile || !values.code || !values.password;
      } else if (loginMethod === "password") {
        hasEmptyFields = !values.mobile || !values.password;
      } else {
        hasEmptyFields = !values.mobile || !values.code;
      }
      const protocolOk = values.agreeTerms === true;
      const isValid = !hasErrors && !hasEmptyFields && protocolOk;
      setIsFormValid(isValid);
      const errorMap: Record<string, boolean> = {};
      errors.forEach((field: any) => {
        const fieldName = field.name[0] as string;
        errorMap[fieldName] = field.errors.length > 0;
      });
      setFormErrors(errorMap);
    }, 0);
  }, [form, loginMethod, mode]);

  const handleGetSmsCode = async () => {
    try {
      const mobile = form.getFieldValue("mobile")?.trim?.() ?? "";
      if (!mobile) {
        form.setFields([
          { name: "mobile", errors: [t("auth.pleaseEnterMobile")] },
        ]);
        return;
      }
      if (!selectedPhoneCode) {
        form.setFields([
          { name: "mobile", errors: [t("auth.pleaseEnterMobile")] },
        ]);
        return;
      }
      const countryCodeDigits = (selectedPhoneCode || "").replace(/\D/g, "");
      const fullPhone = countryCodeDigits
        ? `${countryCodeDigits}${mobile}`
        : mobile;
      const validationResult = validatePhone(fullPhone, selectedCountryId);
      if (!validationResult.valid) {
        form.setFields([
          {
            name: "mobile",
            errors: [t("auth.pleaseEnterValidMobile")],
          },
        ]);
        return;
      }
      setSmsLoading(true);
      const processedMobile = processPhone(mobile);
      AdUtils.onVerifyCodeBegin();
      const smsType = mode === "register" ? "register" : "login";
      const response = await sendSmsCode(
        processedMobile,
        smsType,
        selectedCountryId
      );
      if (response.status === 200) {
        reset();
        start();
      } else {
        throw new Error(response.message || t("auth.sendSmsFailed"));
      }
    } catch (error) {
      console.error("Failed to get SMS code:", error);
      const errorMessage =
        error instanceof Error ? error.message : t("auth.sendSmsFailed");
      form.setFields([{ name: "mobile", errors: [errorMessage] }]);
    } finally {
      setSmsLoading(false);
    }
  };

  const handleAuthSuccess = async (
    user: import("../../modules/user/types").User,
    extra?: { pixelEvent?: any },
    isRegister = false
  ) => {
    if (isRegister) {
      AdUtils.onRegister(extra?.pixelEvent);
      utils.setObject("invitedCode", null);
      utils.setObject("has_registered", null);
      utils.setObject("__auuid", null);
      utils.setObject("__uid", null);
      document.cookie = "__auuid=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = "__uid=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
    } else {
      AdUtils.onLogin();
    }

    const userId = user.userId || utils.getObject("user")?.userId;
    trackingService.trackLoginSuccess(userId).catch(err => {
      console.error("Failed to track LOGIN_SUCCESS:", err);
    });

    utils.setObject("has_registered", null);

    try {
      await wsUtils.open("global");
    } catch (error) {
      console.error("Failed to reinitialize WebSocket after login:", error);
    }

    if (enableWebPush && isWebPushSupported()) {
      try {
        const { permission } = await getPushSubscriptionStatus();
        if (permission === "granted") {
          subscribeHasLogin();
        }
      } catch (error) {
        console.error("WebPush subscription failed:", error);
      }
    }

    setVisible(false);
    form.resetFields();
    setImageCode("");

    if (fromPath) {
      navigate.navigate(fromPath, { replace: true });
      setFromPath(null);
    }
  };

  const doLogin = async (values: LoginRegisterFormValues) => {
    try {
      trackingService.trackLoginStart().catch(err => {
        console.error("Failed to track LOGIN_START:", err);
      });

      if (enableWafCode && !wafToken) {
        Toast.failed(t("auth.pleaseCompleteVerification"));
        return;
      }

      const processedMobile =
        loginMethod === "sms" && values.mobile
          ? processPhone(values.mobile)
          : values.mobile;

      const processedUsername =
        loginMethod === "password" && values.mobile
          ? values.mobile.replace(/^0+/, "")
          : values.mobile;

      const deviceId = utils.getDeviceId();

      let response;
      if (loginMethod === "password") {
        const loginData = {
          username: processedUsername!,
          password: values.password!,
          remember: true,
          loginType: "username" as const,
          wafCode: enableWafCode ? wafToken : undefined,
          imageCode: enableLoginImageCode ? imageCode : undefined,
        } as LoginRequest & {
          deviceId?: string;
          imageCode?: string;
        };
        if (deviceId) {
          loginData.deviceId = deviceId;
        }
        response = await login(loginData);
      } else {
        // SMS Tab：走 /api/auth/phone_login，后端对未注册号码自动建号 + 静默登录。
        // firebaseRegister=true 时即"刚自动注册"，handleAuthSuccess 用它走注册成功埋点。
        const loginData = {
          username: processedUsername!,
          phone: processedMobile!,
          code: values.code,
          remember: values.remember,
          loginType: "mobile" as const,
          countryId: selectedCountryId,
          inviteCode: inviteCode || undefined,
          wafCode: enableWafCode ? wafToken : undefined,
          imageCode: enableLoginImageCode ? imageCode : undefined,
        } as LoginRequest & {
          deviceId?: string;
          inviteCode?: string;
          imageCode?: string;
        };
        if (deviceId) {
          loginData.deviceId = deviceId;
        }
        response = await login(loginData);
      }

      if (response && response.user) {
        await handleAuthSuccess(
          response.user,
          response.extra,
          !!response.firebaseRegister
        );
      } else {
        trackingService.trackLoginFailed("Login failed").catch(err => {
          console.error("Failed to track LOGIN_FAILED:", err);
        });
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : t("auth.loginFailed");
      trackingService.trackLoginFailed(errorMsg).catch(err => {
        console.error("Failed to track LOGIN_FAILED:", err);
      });
      Toast.failed(errorMsg);
    }
  };

  const doRegister = async (values: LoginRegisterFormValues) => {
    try {
      if (enableWafCode && !wafToken) {
        Toast.failed(t("auth.pleaseCompleteVerification"));
        return;
      }

      const processedMobile = values.mobile ? processPhone(values.mobile) : "";
      const deviceId = utils.getDeviceId();

      const registerData: RegisterRequest & {
        deviceId?: string;
        imageCode?: string;
      } = {
        phone: processedMobile,
        password: values.password!,
        confirmPassword: values.password!,
        code: values.code,
        countryId: selectedCountryId,
        inviteCode: inviteCode || undefined,
        agreeTerms: values.agreeTerms,
        remember: true,
        wafCode: enableWafCode ? wafToken : undefined,
        imageCode: enableRegisterImageCode ? imageCode : undefined,
      };

      if (deviceId) {
        registerData.deviceId = deviceId;
      }

      const response = await register(registerData);

      if (response && response.user) {
        await handleAuthSuccess(response.user, response.extra, true);
      }
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : t("auth.registrationFailed");
      Toast.failed(errorMsg);
    }
  };

  const handleSubmit = (values: LoginRegisterFormValues) => {
    if (mode === "register") {
      triggerAgreementConfirmation(() => doRegister(values), undefined);
    } else {
      triggerAgreementConfirmation(() => doLogin(values), undefined);
    }
  };

  const handleClose = () => {
    setVisible(false);
    setFromPath(null);
    form.resetFields();
    setImageCode("");
  };

  const handleTabChange = (key: string) => {
    setLoginMethod(key as LoginRegisterTab);
    form.resetFields();
    setImageCode("");
    setIsFormValid(false);
    setFormErrors({});
  };

  const isSubmitLoading = mode === "register" ? registerLoading : loginLoading;

  // 任一忘记密码子流程激活时，隐藏登录弹窗，避免 modal 叠层
  const isForgotPasswordFlowActive =
    passwordRecoveryVisible ||
    resetPasswordVisible ||
    resetPasswordByEmailVisible ||
    tooManyAttemptsVisible;

  return (
    <>
      <AntdMobilePopup
        visible={visible && !isForgotPasswordFlowActive}
        onClose={handleClose}
        position="bottom"
        className={`login-register-popup ${className}`}
        bodyClassName="login-register-popup__body"
        maskClassName="login-register-popup__mask"
        closeOnSwipe={false}
        destroyOnClose
      >
        <div className="login-register-popup__container">
          {/* Close button */}
          <button
            className="login-register-popup__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>

          {/* Scrollable content area */}
          <div className="login-register-popup__scroll">
            {/* Tabs: login = Password/SMS switchable; register = SMS only (fixed) */}
            <div className="login-register-popup__tabs">
              {mode === "login" ? (
                <Tabs
                  activeKey={loginMethod}
                  align="left"
                  onChange={handleTabChange}
                >
                  <Tab key="password" title={t("auth.passwordTab")} />
                  {enableCodeLogin && (
                    <Tab key="sms" title={t("auth.smsTab")} />
                  )}
                </Tabs>
              ) : (
                <Tabs activeKey="sms" align="left">
                  <Tab key="sms" title={t("auth.smsTab")} />
                </Tabs>
              )}
            </div>

            {/* Form */}
            <div className="login-register-popup__form">
              <Form
                form={form}
                onFinish={handleSubmit}
                onFieldsChange={handleFieldsChange}
                layout="vertical"
                className="login-register-form"
              >
                <Space direction="vertical" style={{ width: "100%" }}>
                  {/* ── Register view ─ fixed SMS form ── */}
                  {mode === "register" && (
                    <>
                      {/* Phone input */}
                      <FormItem
                        name="mobile"
                        rules={[
                          {
                            required: true,
                            message: t("auth.pleaseEnterMobile"),
                          },
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              if (!selectedPhoneCode) return Promise.resolve();
                              const countryCodeDigits = (
                                selectedPhoneCode || ""
                              ).replace(/\D/g, "");
                              const fullPhone = countryCodeDigits
                                ? `${countryCodeDigits}${value}`
                                : value;
                              if (!phonePattern.test(fullPhone)) {
                                return Promise.reject(
                                  new Error(t("auth.pleaseEnterValidMobile"))
                                );
                              }
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <EnhancedInput
                          placeholder={t("auth.mobile")}
                          prefixIcon={{
                            icon: (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <CountryCodeSelect
                                  value={selectedCountryId}
                                  onChange={(countryId, phoneCode) => {
                                    setSelectedCountryId(countryId);
                                    setSelectedPhoneCode(phoneCode);
                                    form.validateFields(["mobile"]);
                                  }}
                                />
                                <span
                                  style={{
                                    color: "var(--color-border-control)",
                                    margin: "0 0.5rem 0.375rem 0",
                                  }}
                                >
                                  |
                                </span>
                                <PhoneIcon />
                              </div>
                            ),
                            variant: "golden",
                            isComplexComponent: true,
                          }}
                          error={formErrors.mobile}
                        />
                      </FormItem>

                      {/* SMS code input */}
                      <FormItem
                        name="code"
                        rules={[
                          {
                            required: true,
                            message: t("auth.pleaseEnterSms"),
                          },
                          { len: 6, message: t("auth.smsCodeLength") },
                        ]}
                      >
                        <EnhancedInput
                          placeholder={
                            t("auth.smsCodePlaceholder") || t("auth.sms")
                          }
                          prefixIcon={{ icon: "message" }}
                          suffixContent={{
                            text: isActive
                              ? t("common.resendIn", { time: timeLeft })
                              : t("common.getCode"),
                            onClick: handleGetSmsCode,
                            loading: smsLoading,
                            disabled: isActive || smsLoading,
                            variant: "secondary",
                            size: "sm",
                            shape: "default",
                            className: "login-register-popup__send-code-btn",
                          }}
                          error={formErrors.code}
                        />
                      </FormItem>

                      {/* Password input */}
                      <FormItem
                        name="password"
                        rules={[
                          {
                            required: true,
                            message: t("auth.pleaseEnterPassword"),
                          },
                          { min: 8, message: t("auth.passwordTooShort") },
                        ]}
                      >
                        <EnhancedInput
                          placeholder={t("auth.password")}
                          type={showPassword ? "text" : "password"}
                          autoComplete="new-password"
                          prefixIcon={{ icon: "password" }}
                          suffixContent={{
                            icon: showPassword ? "eye" : "eye-closed",
                            onClick: () => setShowPassword(!showPassword),
                          }}
                          error={formErrors.password}
                        />
                      </FormItem>

                      {/* 图形验证码 */}
                      {enableRegisterImageCode && (
                        <FormItem name="imageCode">
                          <ImageCode
                            value={imageCode}
                            onChange={setImageCode}
                            error={!!formErrors.imageCode}
                          />
                        </FormItem>
                      )}
                    </>
                  )}

                  {/* ── Login view ── */}
                  {mode === "login" && (
                    <>
                      {/* Phone / account input — adapts per tab */}
                      {loginMethod === "sms" ? (
                        <FormItem
                          name="mobile"
                          rules={[
                            {
                              required: true,
                              message: t("auth.pleaseEnterMobile"),
                            },
                            {
                              validator: (_, value) => {
                                if (!value) return Promise.resolve();
                                if (!selectedPhoneCode)
                                  return Promise.resolve();
                                const countryCodeDigits = (
                                  selectedPhoneCode || ""
                                ).replace(/\D/g, "");
                                const fullPhone = countryCodeDigits
                                  ? `${countryCodeDigits}${value}`
                                  : value;
                                if (!phonePattern.test(fullPhone)) {
                                  return Promise.reject(
                                    new Error(t("auth.pleaseEnterValidMobile"))
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <EnhancedInput
                            placeholder={t("auth.mobile")}
                            prefixIcon={{
                              icon: (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <CountryCodeSelect
                                    value={selectedCountryId}
                                    onChange={(countryId, phoneCode) => {
                                      setSelectedCountryId(countryId);
                                      setSelectedPhoneCode(phoneCode);
                                      form.validateFields(["mobile"]);
                                    }}
                                  />
                                  <span
                                    style={{
                                      color: "var(--color-border-control)",
                                      margin: "0 0.5rem 0.375rem 0",
                                    }}
                                  >
                                    |
                                  </span>
                                  <PhoneIcon />
                                </div>
                              ),
                              variant: "golden",
                              isComplexComponent: true,
                            }}
                            error={formErrors.mobile}
                          />
                        </FormItem>
                      ) : (
                        <FormItem
                          name="mobile"
                          rules={[
                            {
                              required: true,
                              message:
                                t("auth.pleaseEnterAccount") ||
                                "Please enter your account",
                            },
                          ]}
                        >
                          <EnhancedInput
                            placeholder={
                              t("auth.loginPlaceholder") ||
                              "Phone / Email / User ID"
                            }
                            prefixIcon={{
                              icon: <UserIcon />,
                              variant: "golden",
                            }}
                            error={formErrors.mobile}
                          />
                        </FormItem>
                      )}

                      {loginMethod === "sms" ? (
                        <>
                          {/* SMS code input with Send button */}
                          <FormItem
                            name="code"
                            rules={[
                              {
                                required: true,
                                message: t("auth.pleaseEnterSms"),
                              },
                              { len: 6, message: t("auth.smsCodeLength") },
                            ]}
                          >
                            <EnhancedInput
                              placeholder={
                                t("auth.smsCodePlaceholder") || t("auth.sms")
                              }
                              prefixIcon={{ icon: "message" }}
                              suffixContent={{
                                text: isActive
                                  ? t("common.resendIn", { time: timeLeft })
                                  : t("common.getCode"),
                                onClick: handleGetSmsCode,
                                loading: smsLoading,
                                disabled: isActive || smsLoading,
                                variant: "secondary",
                                size: "sm",
                                shape: "default",
                                className:
                                  "login-register-popup__send-code-btn",
                              }}
                              error={formErrors.code}
                            />
                          </FormItem>
                        </>
                      ) : (
                        <>
                          {/* Password input */}
                          <FormItem
                            name="password"
                            rules={[
                              {
                                required: true,
                                message: t("auth.pleaseEnterPassword"),
                              },
                              { min: 8, message: t("auth.passwordTooShort") },
                            ]}
                          >
                            <EnhancedInput
                              placeholder={t("auth.password")}
                              type={showPassword ? "text" : "password"}
                              autoComplete="current-password"
                              prefixIcon={{ icon: "password" }}
                              suffixContent={{
                                icon: showPassword ? "eye" : "eye-closed",
                                onClick: () => setShowPassword(!showPassword),
                              }}
                              error={formErrors.password}
                            />
                          </FormItem>

                          {/* Forgot password */}
                          {(loginRegisterConfig?.enableMobileResetPassword ||
                            loginRegisterConfig?.enableEmailResetPassword) && (
                            <div className="login-register-popup__forgot-row">
                              <AntdLink
                                variant="primary"
                                underline
                                onClick={() => {
                                  const phoneOn =
                                    !!loginRegisterConfig?.enableMobileResetPassword;
                                  const emailOn =
                                    !!loginRegisterConfig?.enableEmailResetPassword;
                                  if (phoneOn && emailOn) {
                                    setPasswordRecoveryVisible(true);
                                  } else if (phoneOn) {
                                    if (resetPasswordIpInCooldown) {
                                      setTooManyAttemptsVisible(true);
                                    } else {
                                      setResetPasswordVisible(true);
                                    }
                                  } else {
                                    if (resetPasswordEmailIpInCooldown) {
                                      setTooManyAttemptsVisible(true);
                                    } else {
                                      setResetPasswordByEmailVisible(true);
                                    }
                                  }
                                }}
                                className="login-register-popup__forgot-link"
                              >
                                {t("auth.forgotPassword") || "Forgot?"}
                              </AntdLink>
                            </div>
                          )}
                        </>
                      )}

                      {/* 图形验证码 */}
                      {enableLoginImageCode && (
                        <FormItem name="imageCode">
                          <ImageCode
                            value={imageCode}
                            onChange={setImageCode}
                            error={!!formErrors.imageCode}
                          />
                        </FormItem>
                      )}
                    </>
                  )}

                  {/* Turnstile */}
                  {enableWafCode && wafSiteKey && (
                    <div className="login-register-popup__turnstile">
                      <Turnstile
                        key={formKey}
                        siteKey={wafSiteKey}
                        onChange={token => setWafToken(token)}
                      />
                    </div>
                  )}

                  {/* Submit button */}
                  <Button
                    type="submit"
                    block
                    size="large"
                    variant="primary"
                    loading={isSubmitLoading}
                    disabled={
                      !isFormValid ||
                      isSubmitLoading ||
                      (enableWafCode && !wafToken)
                    }
                    className="login-register-popup__submit-btn"
                  >
                    {mode === "register"
                      ? t("auth.register") || "Register"
                      : t("auth.login") || "Login"}
                  </Button>

                  {/* 协议：未勾选时提交按钮置灰 */}
                  <div className="login-register-popup__terms">
                    <FormItem
                      name="agreeTerms"
                      valuePropName="checked"
                      initialValue={enableProtocolText}
                      rules={[
                        {
                          validator: (_, value) =>
                            value
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error(t("auth.agreeTermsRequired"))
                                ),
                        },
                      ]}
                    >
                      <Checkbox variant="golden">
                        <span
                          className="login-register-popup__terms-text"
                          dangerouslySetInnerHTML={{ __html: protocolHtml }}
                          onClick={handleProtocolLinkClick}
                        />
                      </Checkbox>
                    </FormItem>
                  </div>

                  {/* Compliance text */}
                  <div className="login-register-popup__compliance">
                    <p className="login-register-popup__compliance-intro">
                      The following personalities are NOT ALLOWED to register
                      and/or play in this online gaming website:
                    </p>
                    <ul className="login-register-popup__compliance-list">
                      <li>
                        Government Official or employee connected directly with
                        the operation of the Government or any of its agencies.
                      </li>
                      <li>
                        Members of the Armed Forces of the Philippines,
                        including the Army, Navy, Air Force, or the Philippine
                        National Police.
                      </li>
                      <li>Persons under 21 years of age.</li>
                      <li>
                        Persons included in the PAGCOR's National Database of
                        Restricted Persons (NDRP).
                      </li>
                      <li>
                        Gaming Employment License (GEL) holder. Funds or credits
                        in the account of player who is found ineligible to play
                        shall mean forfeiture of said funds/credits in favor of
                        the Government.
                      </li>
                    </ul>
                    <p className="login-register-popup__compliance-footer">
                      Keep online gaming private. Avoid playing online games in
                      open and public spaces.
                    </p>
                  </div>

                  <div className="login-register-popup__coop-top" aria-hidden>
                    <FooterCooperationPlatformsTopRow />
                  </div>

                  {/* PAGCOR logos */}
                  <div className="login-register-popup__pagcor">
                    <div className="login-register-popup__pagcor-logos">
                      <img
                        src="/assets/pagcor-logo.png"
                        alt="PAGCOR"
                        className="login-register-popup__pagcor-img"
                        onError={e => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                      <img
                        src="/assets/21plus-logo.png"
                        alt="21+"
                        className="login-register-popup__pagcor-img"
                        onError={e => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    </div>
                    <p className="login-register-popup__powered-by">
                      Powered by DigiPlus, a Fortune Southeast Asia 500 company
                    </p>
                  </div>
                </Space>
              </Form>
            </div>
          </div>
        </div>
      </AntdMobilePopup>

      {/* Protocol Dialog */}
      <ProtocolDialog
        visible={protocolDialogVisible}
        onClose={() => setProtocolDialogVisible(false)}
        protocolPath={currentProtocolPath}
      />

      {/* Password Recovery Dialogs */}
      {loginRegisterConfig?.enableMobileResetPassword &&
        loginRegisterConfig?.enableEmailResetPassword && (
          <PasswordRecoveryDialog
            visible={passwordRecoveryVisible}
            onClose={() => setPasswordRecoveryVisible(false)}
            onSelectMethod={method => {
              setPasswordRecoveryVisible(false);
              if (method === "phone") {
                if (resetPasswordIpInCooldown) {
                  setTooManyAttemptsVisible(true);
                } else {
                  setResetPasswordVisible(true);
                }
              } else {
                if (resetPasswordEmailIpInCooldown) {
                  setTooManyAttemptsVisible(true);
                } else {
                  setResetPasswordByEmailVisible(true);
                }
              }
            }}
          />
        )}

      {loginRegisterConfig?.enableMobileResetPassword && (
        <ResetPasswordDialog
          visible={resetPasswordVisible}
          onClose={() => setResetPasswordVisible(false)}
          onSuccess={() => {
            setResetPasswordVisible(false);
            setPasswordRecoveryVisible(false);
          }}
          resetMethods={loginRegisterConfig?.resetPasswordMethods}
          onTooManyAttempts={() => {
            setPasswordRecoveryVisible(false);
            setTooManyAttemptsVisible(true);
          }}
        />
      )}

      {loginRegisterConfig?.enableEmailResetPassword && (
        <ResetPasswordByEmailDialog
          visible={resetPasswordByEmailVisible}
          onClose={() => setResetPasswordByEmailVisible(false)}
          onSuccess={() => {
            setResetPasswordByEmailVisible(false);
            setPasswordRecoveryVisible(false);
          }}
          onTooManyAttempts={() => {
            setPasswordRecoveryVisible(false);
            setTooManyAttemptsVisible(true);
          }}
        />
      )}

      <TooManyPasswordAttemptsDialog
        visible={tooManyAttemptsVisible}
        onClose={() => {
          setTooManyAttemptsVisible(false);
          useHomeDisplayStore.getState().refreshConfig();
        }}
        supportUrl={onlineServiceSupportUrl}
      />
    </>
  );
};

export default LoginRegisterPopup;
